import { useState, useCallback, useRef } from 'react';
import * as Cesium from 'cesium';
import { exportJsonFile, importJsonFile } from '../utils/fileExportUtils';

/**
 * Custom hook for managing viewpoint bookmarks in Cesium viewer
 * Handles saving and restoring camera positions and orientations
 */
export const useViewpoints = (viewer) => {
  const [viewpoints, setViewpoints] = useState([]);
  const [highlightedViewpoint, setHighlightedViewpoint] = useState(null);
  const pyramidsRef = useRef(new Map()); // Store pyramid entities by viewpoint ID

  /**
   * Generates a unique ID for a new viewpoint
   */
  const generateId = useCallback(() => {
    return `viewpoint_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }, []);

  /**
   * Gets the current camera state from the Cesium viewer
   */
  const getCurrentCameraState = useCallback(() => {
    if (!viewer || !viewer.camera) {
      return null;
    }

    const camera = viewer.camera;
    const position = camera.position.clone();
    const direction = camera.direction.clone();
    const up = camera.up.clone();
    const right = camera.right.clone();
    const heading = camera.heading;
    const pitch = camera.pitch;
    const roll = camera.roll;

    return {
      position: {
        x: position.x,
        y: position.y,
        z: position.z
      },
      direction: {
        x: direction.x,
        y: direction.y,
        z: direction.z
      },
      up: {
        x: up.x,
        y: up.y,
        z: up.z
      },
      right: {
        x: right.x,
        y: right.y,
        z: right.z
      },
      heading,
      pitch,
      roll
    };
  }, [viewer]);

  /**
   * Creates a camera frustum pyramid entity to visualize a viewpoint
   */
  const createPyramidEntity = useCallback((viewpoint) => {
    if (!viewer || !viewer.entities || !viewpoint.cameraState) {
      return null;
    }

    const { position, direction, up, right } = viewpoint.cameraState;
    const cameraPosition = new Cesium.Cartesian3(position.x, position.y, position.z);
    
    // Calculate adaptive size based on dataset bounding box
    const scene = viewer.scene;
    const globe = scene.globe;
    
    // Get the scene's bounding sphere to determine dataset scale
    let datasetScale = 100.0; // Default scale
    if (scene.primitives && scene.primitives.length > 0) {
      // Try to get bounding sphere from primitives (point clouds, 3D tilesets)
      for (let i = 0; i < scene.primitives.length; i++) {
        const primitive = scene.primitives.get(i);
        if (primitive.boundingSphere) {
          const radius = primitive.boundingSphere.radius;
          if (radius > 0) {
            datasetScale = radius;
            break;
          }
        }
      }
    }
    
    // Scale pyramid size based on dataset size (smaller for small datasets, larger for large ones)
    const scaleFactor = Math.max(datasetScale * 0.01, 0.5); // 1% of dataset size, minimum 2m
    const frustumDepth = Math.min(scaleFactor, 50.0); // Maximum 50m to prevent overly large pyramids
    const fovRadians = Cesium.Math.toRadians(30); // Very narrow FOV for small pyramid
    const aspectRatio = 1.33; // 4:3 aspect ratio
    
    // Calculate frustum size at the far plane based on FOV
    const halfHeight = Math.tan(fovRadians * 0.5) * frustumDepth;
    const halfWidth = halfHeight * aspectRatio;
    
    // Use the saved camera direction vectors directly
    const forward = new Cesium.Cartesian3(direction.x, direction.y, direction.z);
    const upVector = new Cesium.Cartesian3(up.x, up.y, up.z);
    const rightVector = new Cesium.Cartesian3(right.x, right.y, right.z);
    
    // Offset the pyramid much further behind the camera position to avoid interference
    const backwardOffset = Cesium.Cartesian3.multiplyByScalar(forward, frustumDepth * 1.0, new Cesium.Cartesian3());
    const pyramidApex = Cesium.Cartesian3.subtract(cameraPosition, backwardOffset, new Cesium.Cartesian3());
    
    // Scale the forward vector to the frustum depth
    const scaledForward = Cesium.Cartesian3.multiplyByScalar(forward, frustumDepth, new Cesium.Cartesian3());
    
    // Calculate base center position (offset apex + forward direction)
    const baseCenter = Cesium.Cartesian3.add(pyramidApex, scaledForward, new Cesium.Cartesian3());
    
    // Calculate the base corner positions
    const rightOffset = Cesium.Cartesian3.multiplyByScalar(rightVector, halfWidth, new Cesium.Cartesian3());
    const upOffset = Cesium.Cartesian3.multiplyByScalar(upVector, halfHeight, new Cesium.Cartesian3());
    
    // Define pyramid vertices in world space (apex offset behind camera, base in viewing direction)
    const apexWorld = pyramidApex; // Offset pyramid apex behind camera position
    const bottomLeftWorld = Cesium.Cartesian3.subtract(
      Cesium.Cartesian3.subtract(baseCenter, rightOffset, new Cesium.Cartesian3()),
      upOffset, new Cesium.Cartesian3()
    );
    const bottomRightWorld = Cesium.Cartesian3.add(
      Cesium.Cartesian3.subtract(baseCenter, rightOffset, new Cesium.Cartesian3()),
      upOffset, new Cesium.Cartesian3()
    );
    const topRightWorld = Cesium.Cartesian3.add(
      Cesium.Cartesian3.add(baseCenter, rightOffset, new Cesium.Cartesian3()),
      upOffset, new Cesium.Cartesian3()
    );
    const topLeftWorld = Cesium.Cartesian3.subtract(
      Cesium.Cartesian3.add(baseCenter, rightOffset, new Cesium.Cartesian3()),
      upOffset, new Cesium.Cartesian3()
    );
    
    // Create the pyramid wireframe with thinner, darker lines
    const wireframe = viewer.entities.add({
      id: `viewpoint_pyramid_wireframe_${viewpoint.id}`,
      polyline: {
        positions: [
          // Base square
          bottomLeftWorld, bottomRightWorld, topRightWorld, topLeftWorld, bottomLeftWorld,
          // Edges to apex
          apexWorld, bottomLeftWorld, apexWorld, bottomRightWorld, 
          apexWorld, topRightWorld, apexWorld, topLeftWorld
        ],
        width: 1, // Much thinner lines
        material: Cesium.Color.BLACK.withAlpha(0.3), // Much darker and more opaque
        clampToGround: false,
        followSurface: false
      }
    });
    
    // Add the four triangular faces of the pyramid (matching the wireframe exactly)
    const face1 = viewer.entities.add({
      id: `viewpoint_pyramid_face1_${viewpoint.id}`,
      polygon: {
        hierarchy: [apexWorld, bottomLeftWorld, bottomRightWorld],
        material: Cesium.Color.LIME.withAlpha(0.2),
        outline: false,
        perPositionHeight: true // Important for proper 3D positioning
      }
    });
    
    const face2 = viewer.entities.add({
      id: `viewpoint_pyramid_face2_${viewpoint.id}`,
      polygon: {
        hierarchy: [apexWorld, bottomRightWorld, topRightWorld],
        material: Cesium.Color.LIME.withAlpha(0.2),
        outline: false,
        perPositionHeight: true
      }
    });
    
    const face3 = viewer.entities.add({
      id: `viewpoint_pyramid_face3_${viewpoint.id}`,
      polygon: {
        hierarchy: [apexWorld, topRightWorld, topLeftWorld],
        material: Cesium.Color.LIME.withAlpha(0.2),
        outline: false,
        perPositionHeight: true
      }
    });
    
    const face4 = viewer.entities.add({
      id: `viewpoint_pyramid_face4_${viewpoint.id}`,
      polygon: {
        hierarchy: [apexWorld, topLeftWorld, bottomLeftWorld],
        material: Cesium.Color.LIME.withAlpha(0.2),
        outline: false,
        perPositionHeight: true
      }
    });
    
    // Add filled base for better visualization
    const baseFill = viewer.entities.add({
      id: `viewpoint_pyramid_base_${viewpoint.id}`,
      polygon: {
        hierarchy: [bottomLeftWorld, bottomRightWorld, topRightWorld, topLeftWorld],
        material: Cesium.Color.LIME.withAlpha(0.2),
        outline: false,
        perPositionHeight: true
      }
    });
    
    // Store all entities for cleanup
    return { wireframe, face1, face2, face3, face4, baseFill };
  }, [viewer]);

  /**
   * Saves the current camera position as a new viewpoint bookmark
   */
  const saveCurrentViewpoint = useCallback((name = null) => {
    const cameraState = getCurrentCameraState();
    if (!cameraState) {
      console.warn('Cannot save viewpoint: viewer not ready');
      return null;
    }

    const timestamp = new Date().toLocaleString();
    const defaultName = `Viewpoint ${timestamp}`;
    const viewpointName = name || defaultName;

    const newViewpoint = {
      id: generateId(),
      name: viewpointName,
      timestamp: Date.now(),
      cameraState,
      displayTimestamp: timestamp
    };

    setViewpoints(prev => [...prev, newViewpoint]);
    return newViewpoint;
  }, [getCurrentCameraState, generateId]);

  /**
   * Restores the camera to a saved viewpoint with smooth animation
   */
  const restoreViewpoint = useCallback((viewpoint, duration = 2.0) => {
    if (!viewer || !viewer.camera || !viewpoint?.cameraState) {
      console.warn('Cannot restore viewpoint: invalid viewer or viewpoint');
      return;
    }

    const { position, heading, pitch, roll } = viewpoint.cameraState;
    
    const destination = new Cesium.Cartesian3(position.x, position.y, position.z);
    
    viewer.camera.flyTo({
      destination,
      orientation: {
        heading,
        pitch,
        roll
      },
      duration,
      complete: () => {
        console.log(`Restored viewpoint: ${viewpoint.name}`);
      }
    });
  }, [viewer]);

  /**
   * Deletes a viewpoint bookmark and its associated pyramid
   */
  const deleteViewpoint = useCallback((viewpointId) => {
    // Remove pyramid entities if they exist
    const pyramidEntities = pyramidsRef.current.get(viewpointId);
    if (pyramidEntities && viewer && viewer.entities) {
      Object.values(pyramidEntities).forEach(entity => {
        if (entity) {
          viewer.entities.remove(entity);
        }
      });
      pyramidsRef.current.delete(viewpointId);
    }
    
    setViewpoints(prev => prev.filter(vp => vp.id !== viewpointId));
  }, [viewer]);

  /**
   * Updates the name of an existing viewpoint
   */
  const updateViewpointName = useCallback((viewpointId, newName) => {
    setViewpoints(prev => prev.map(vp => 
      vp.id === viewpointId ? { ...vp, name: newName } : vp
    ));
  }, []);

  /**
   * Exports viewpoints to JSON format
   */
  const exportViewpoints = useCallback(() => {
    const data = {
      viewpoints,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    exportJsonFile(`cesium_viewpoints_${new Date().toISOString().split('T')[0]}.json`, data);
  }, [viewpoints]);

  /**
   * Imports viewpoints from JSON file
   */
  const importViewpoints = useCallback((file) => {
    return importJsonFile(file).then((data) => {
      if (data.viewpoints && Array.isArray(data.viewpoints)) {
        setViewpoints(prev => [...prev, ...data.viewpoints]);
        return data.viewpoints.length;
      }
      throw new Error('Invalid viewpoints file format');
    });
  }, []);

  /**
   * Clears all saved viewpoints and their pyramids
   */
  const clearAllViewpoints = useCallback(() => {
    // Remove all pyramid entities
    if (viewer && viewer.entities) {
      pyramidsRef.current.forEach((pyramidEntities) => {
        Object.values(pyramidEntities).forEach(entity => {
          if (entity) {
            viewer.entities.remove(entity);
          }
        });
      });
    }
    pyramidsRef.current.clear();
    
    setViewpoints([]);
  }, [viewer]);

  /**
   * Shows pyramid markers for all viewpoints
   */
  const showPyramids = useCallback(() => {
    if (!viewer || !viewer.entities) {
      return;
    }

    viewpoints.forEach((viewpoint) => {
      // Only create pyramid if it doesn't already exist
      if (!pyramidsRef.current.has(viewpoint.id)) {
        const pyramidEntities = createPyramidEntity(viewpoint);
        if (pyramidEntities) {
          pyramidsRef.current.set(viewpoint.id, pyramidEntities);
        }
      }
    });
  }, [viewer, viewpoints, createPyramidEntity]);

  /**
   * Hides all pyramid markers
   */
  const hidePyramids = useCallback(() => {
    if (!viewer || !viewer.entities) {
      return;
    }

    pyramidsRef.current.forEach((pyramidEntities) => {
      Object.values(pyramidEntities).forEach(entity => {
        if (entity) {
          viewer.entities.remove(entity);
        }
      });
    });
    pyramidsRef.current.clear();
    setHighlightedViewpoint(null);
  }, [viewer]);

  /**
   * Highlights a specific pyramid by changing its color
   */
  const highlightPyramid = useCallback((viewpointId) => {
    if (!viewer || !viewer.entities) {
      return;
    }

    // Reset all pyramids to normal color first
    pyramidsRef.current.forEach((pyramidEntities, id) => {
      const isHighlighted = id === viewpointId;
      const color = isHighlighted ? Cesium.Color.LIME : Cesium.Color.LIME;
      const alpha = isHighlighted ? 0.5 : 0.2;
      const wireframeColor = isHighlighted ? Cesium.Color.LIME.withAlpha(0.8) : Cesium.Color.BLACK.withAlpha(0.3);
      const wireframeWidth = isHighlighted ? 2 : 1;

      Object.values(pyramidEntities).forEach(entity => {
        if (entity && entity.polygon) {
          entity.polygon.material = color.withAlpha(alpha);
        }
        if (entity && entity.polyline) {
          entity.polyline.material = wireframeColor;
          entity.polyline.width = wireframeWidth;
        }
      });
    });

    setHighlightedViewpoint(viewpointId);
  }, [viewer]);

  /**
   * Removes highlighting from all pyramids
   */
  const clearPyramidHighlight = useCallback(() => {
    highlightPyramid(null);
  }, [highlightPyramid]);

  return {
    viewpoints,
    highlightedViewpoint,
    saveCurrentViewpoint,
    restoreViewpoint,
    deleteViewpoint,
    updateViewpointName,
    exportViewpoints,
    importViewpoints,
    clearAllViewpoints,
    showPyramids,
    hidePyramids,
    highlightPyramid,
    clearPyramidHighlight
  };
};